import { Table as TanstackTable } from '@tanstack/react-table';
import { useCallback } from 'react';

export interface UsePaginationOptions {
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export interface PaginationState {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalRows: number;
  startRow: number;
  endRow: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
}

export interface PaginationControls {
  goToPage: (page: number) => void;
  previousPage: () => void;
  nextPage: () => void;
  setPageSize: (size: number) => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
}

export type UsePaginationReturn = PaginationState & PaginationControls;

export function usePagination<T>(
  table: TanstackTable<T>,
  options: UsePaginationOptions = {}
): UsePaginationReturn {
  const { onPageChange, onPageSizeChange } = options;

  const state = table.getState().pagination;
  const currentPage = state.pageIndex + 1;
  const totalPages = table.getPageCount();
  const pageSize = state.pageSize;
  const totalRows = table.getFilteredRowModel().rows.length;

  const startRow = state.pageIndex * pageSize + 1;
  const endRow = Math.min(startRow + pageSize - 1, totalRows);

  const goToPage = useCallback(
    (page: number) => {
      const pageIndex = page - 1;
      table.setPageIndex(pageIndex);
      onPageChange?.(page);
    },
    [table, onPageChange]
  );

  const previousPage = useCallback(() => {
    table.previousPage();
    onPageChange?.(currentPage - 1);
  }, [table, currentPage, onPageChange]);

  const nextPage = useCallback(() => {
    table.nextPage();
    onPageChange?.(currentPage + 1);
  }, [table, currentPage, onPageChange]);

  const setPageSize = useCallback(
    (size: number) => {
      table.setPageSize(size);
      onPageSizeChange?.(size);
    },
    [table, onPageSizeChange]
  );

  const goToFirstPage = useCallback(() => {
    goToPage(1);
  }, [goToPage]);

  const goToLastPage = useCallback(() => {
    goToPage(totalPages);
  }, [goToPage, totalPages]);

  return {
    // State
    currentPage,
    totalPages,
    pageSize,
    totalRows,
    startRow: totalRows === 0 ? 0 : startRow,
    endRow: totalRows === 0 ? 0 : endRow,
    canPreviousPage: table.getCanPreviousPage(),
    canNextPage: table.getCanNextPage(),

    // Controls
    goToPage,
    previousPage,
    nextPage,
    setPageSize,
    goToFirstPage,
    goToLastPage,
  };
}
